import { SalesInsightsActivitiesSummary, SalesInsightsCustomerHealthSummary } from '../models/sales-insights.model';
import { formatSalesInsightsAmount, formatSalesInsightsPercent } from './sales-insights-format';

export type SalesInsightsTileTone = 'neutral' | 'success' | 'warning' | 'danger' | 'muted';

export interface SalesInsightsTileText {
  key?: string;
  params?: { [name: string]: string };
  text?: string;
}

export interface SalesInsightsStatTile {
  key: string;
  labelKey: string;
  // A label that is data rather than a translation key, such as an activity type's name.
  label: string | null;
  value: string;
  sub: SalesInsightsTileText | null;
  tone: SalesInsightsTileTone;
  progress: number | null;
  progressLabel: string | null;
  flag: string | null;
  icon: string;
  // The activity type a per-type tile counts, so the component can give it the type's icon and colour.
  type: string | null;
}

export type SalesInsightsTileSummary = SalesInsightsCustomerHealthSummary | SalesInsightsActivitiesSummary;

const SEGMENT_TONES: { [key: string]: SalesInsightsTileTone } = {
  visitRequired: 'success',
  noVisitRequired: 'success',
  noPurchase3m: 'warning',
  noPurchase1m: 'warning',
  noContact6m: 'muted',
  overdue: 'danger'
};

const TILE_ICONS: { [key: string]: string } = {
  visitRequired: 'fa-solid fa-location-dot',
  noVisitRequired: 'fa-solid fa-building',
  noPurchase3m: 'fa-solid fa-cart-arrow-down',
  noPurchase1m: 'fa-solid fa-cart-arrow-down',
  noContact6m: 'fa-solid fa-phone-slash',
  overdue: 'fa-solid fa-clock',
  newCustomers: 'fa-solid fa-user-plus',
  lostCustomers: 'fa-solid fa-user-minus',
  plannedNext7Days: 'fa-solid fa-calendar-week',
  doneThisMonth: 'fa-solid fa-circle-check',
  visitGoalThisMonth: 'fa-solid fa-bullseye',
  visitsLeadingToOrder: 'fa-solid fa-cart-shopping',
  notVisited90Days: 'fa-solid fa-store-slash'
};
const DEFAULT_TILE_ICON = 'fa-solid fa-chart-simple';
const PLANNED_BY_TYPE = 'plannedByType';

const ACTIVE_SEGMENTS = ['visitRequired', 'noVisitRequired'];
const ACTIVITY_TILES = ['plannedNext7Days', 'doneThisMonth', 'visitsLeadingToOrder', 'notVisited90Days'];

export function salesInsightsConstantCase(key: string): string {
  return key
    .replace(/([A-Z])/g, '_$1')
    .replace(/(\d+)/g, '_$1')
    .toUpperCase();
}

export function salesInsightsTileLabelKey(key: string): string {
  return `SALES_INSIGHTS_TILE_${salesInsightsConstantCase(key)}`;
}

export function salesInsightsStatTiles(
  keys: string[] | null | undefined,
  data: SalesInsightsTileSummary | null,
  typeName: (type: string) => string = type => type
): SalesInsightsStatTile[] {
  if (!data) {
    return [];
  }
  const order = keys?.length ? keys : defaultKeys(data);
  const byTypeTiles = order.includes(PLANNED_BY_TYPE);
  return order
    .flatMap(key => {
      if (isCustomerHealth(data)) {
        return [customerHealthTile(key, data)];
      }
      if (key === PLANNED_BY_TYPE) {
        return plannedByTypeTiles(data as SalesInsightsActivitiesSummary, typeName);
      }
      return [activitiesTile(key, data as SalesInsightsActivitiesSummary, typeName, byTypeTiles)];
    })
    .filter((tile): tile is SalesInsightsStatTile => !!tile);
}

// One tile per type planned in the coming week, largest first, labelled with the type's name.
function plannedByTypeTiles(
  data: SalesInsightsActivitiesSummary,
  typeName: (type: string) => string
): SalesInsightsStatTile[] {
  return Object.entries(data.plannedByType || {})
    .filter(([, count]) => count > 0)
    .sort((a, b) => b[1] - a[1])
    .map(([type, count]) => ({
      ...tile(`${PLANNED_BY_TYPE}.${type}`, count, {
        sub: textKey('SALES_INSIGHTS_TILE_PLANNED_TYPE_SUB'),
        type: type || null
      }),
      labelKey: type ? salesInsightsTileLabelKey(PLANNED_BY_TYPE) : 'SALES_INSIGHTS_TILE_PLANNED_WITHOUT_TYPE',
      label: type ? typeName(type) || type : null
    }));
}

function isCustomerHealth(data: SalesInsightsTileSummary): data is SalesInsightsCustomerHealthSummary {
  return Array.isArray((data as SalesInsightsCustomerHealthSummary).segments);
}

function defaultKeys(data: SalesInsightsTileSummary): string[] {
  return isCustomerHealth(data)
    ? [...data.segments.map(segment => segment.key), 'newCustomers', 'lostCustomers']
    : ACTIVITY_TILES;
}

function customerHealthTile(key: string, data: SalesInsightsCustomerHealthSummary): SalesInsightsStatTile | null {
  const segment = data.segments.find(s => s.key === key);
  if (segment) {
    const active = data.segments
      .filter(s => ACTIVE_SEGMENTS.includes(s.key))
      .reduce((sum, s) => sum + (s.count || 0), 0);
    return tile(key, segment.count, {
      tone: SEGMENT_TONES[key] || 'neutral',
      flag: key,
      sub:
        active > 0
          ? textKey('SALES_INSIGHTS_TILE_SHARE_OF_CUSTOMERS', {
              percent: formatSalesInsightsPercent((segment.count / active) * 100)
            })
          : null
    });
  }
  if (key === 'newCustomers') {
    return tile(key, data.newCustomers, { tone: 'success' });
  }
  if (key === 'lostCustomers') {
    return tile(key, data.lostCustomers, { tone: data.lostCustomers > 0 ? 'danger' : 'neutral' });
  }
  return unknownTile(key, data);
}

function activitiesTile(
  key: string,
  data: SalesInsightsActivitiesSummary,
  typeName: (type: string) => string,
  byTypeTiles = false
): SalesInsightsStatTile | null {
  switch (key) {
    case 'plannedNext7Days': {
      if (byTypeTiles) {
        return tile(key, data.plannedNext7Days, {});
      }
      const byType = Object.entries(data.plannedByType || {})
        .filter(([, count]) => count > 0)
        .sort((a, b) => b[1] - a[1])
        .map(([type, count]) => `${formatSalesInsightsAmount(count)} ${typeName(type)}`)
        .join(' · ');
      return tile(key, data.plannedNext7Days, { sub: byType ? { text: byType } : null });
    }
    case 'doneThisMonth': {
      const goal = data.visitGoalThisMonth;
      return tile(key, data.doneThisMonth, {
        sub: goal ? textKey('SALES_INSIGHTS_TILE_OF_GOAL', { goal: formatSalesInsightsAmount(goal) }) : null,
        progress: goal ? Math.min(100, ((data.doneThisMonth || 0) / goal) * 100) : null,
        progressLabel: goal ? formatSalesInsightsPercent(((data.doneThisMonth || 0) / goal) * 100) : null
      });
    }
    case 'visitGoalThisMonth': {
      const goal = data.visitGoalThisMonth;
      if (!goal) {
        return null;
      }
      const done = data.doneThisMonth || 0;
      return tile(key, done, {
        sub: textKey('SALES_INSIGHTS_TILE_OF_GOAL', { goal: formatSalesInsightsAmount(goal) }),
        progress: Math.min(100, (done / goal) * 100),
        progressLabel: formatSalesInsightsPercent((done / goal) * 100)
      });
    }
    case 'visitsLeadingToOrder': {
      const visits = data.visitsLeadingToOrder?.visits || 0;
      const withOrder = data.visitsLeadingToOrder?.withOrder || 0;
      return tile(key, withOrder, {
        progress: visits ? Math.min(100, (withOrder / visits) * 100) : null,
        progressLabel: visits ? formatSalesInsightsPercent((withOrder / visits) * 100) : null,
        sub: visits
          ? textKey('SALES_INSIGHTS_TILE_OF_VISITS_SHORT', { visits: formatSalesInsightsAmount(visits) })
          : textKey('SALES_INSIGHTS_TILE_NO_VISITS')
      });
    }
    case 'notVisited90Days':
      return tile(key, data.notVisited90Days, {
        tone: data.notVisited90Days > 0 ? 'danger' : 'neutral',
        sub: textKey('SALES_INSIGHTS_TILE_VISIT_CUSTOMERS')
      });
    default:
      return unknownTile(key, data);
  }
}

function unknownTile(key: string, data: SalesInsightsTileSummary): SalesInsightsStatTile | null {
  const value = (data as unknown as { [key: string]: unknown })[key];
  return typeof value === 'number' ? tile(key, value, {}) : null;
}

function tile(key: string, count: number | null, extra: Partial<SalesInsightsStatTile>): SalesInsightsStatTile {
  return {
    key,
    labelKey: salesInsightsTileLabelKey(key),
    label: null,
    value: count === null || count === undefined ? '' : formatSalesInsightsAmount(count),
    sub: null,
    tone: 'neutral',
    progress: null,
    progressLabel: null,
    flag: null,
    icon: TILE_ICONS[key] || DEFAULT_TILE_ICON,
    type: null,
    ...extra
  };
}

function textKey(key: string, params?: { [name: string]: string }): SalesInsightsTileText {
  return params ? { key, params } : { key };
}
